import {useSelector} from 'react-redux';

import appConfig from '@/config/appConfig.typed';
import ROUTE_PATHS from '@/consts/route/routePaths';
import useLocationPathname from '@/hooks/useLocationPathname';
import aemClientService from '@/services/aem/aemClientService';
import appErrorService from '@/services/app/appErrorService';
import iccMarketService from '@/services/icc/iccMarketService';
import languageDataService from '@/services/localization/languageDataService';
import {initLocalizations} from '@/services/localization/localizationService';
import marketService from '@/services/marketService';
import appRouterService from '@/services/route/appRouterService';
import routerService from '@/services/route/routerService';
import tncService from '@/services/tncService';
import {selectIsLoggedIn} from '@/state/selectors/auth';
import {selectIsAem, selectLanguageCode} from '@/state/selectors/global';

const useLanguageSelect = () => {
    const marketLanguages = appConfig.getMarketLanguages();

    const languageCode = useSelector(selectLanguageCode);
    const languageName = getLanguageName(marketLanguages, languageCode);
    const selectListData = getSelectListData(marketLanguages);
    const handleLanguageChange = useHandleLanguageChange();

    return {languageCode, languageName, selectListData, handleLanguageChange};
};

const getLanguageName = (marketLanguages, languageCode) => {
    const marketLanguage = languageDataService.getMarketLanguageByLanguageCode(marketLanguages, languageCode);
    const {languageTitle} = marketLanguage || {};
    const languageName = languageTitle || '';

    return languageName;
};

const getSelectListData = (marketLanguages) => {
    return marketLanguages.map(({languageTitle, languageCode}) => ({
        title: languageTitle,
        value: languageCode,
    }));
};

const useHandleLanguageChange = () => {
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const isAem = useSelector(selectIsAem);
    const {pathname} = useLocationPathname();
    const isInstallPwaPage = pathname === ROUTE_PATHS.INSTALL_PWA;

    const handleLanguageChange = async (languageCode) => {
        try {
            //call api method before consumer profile and then setup language only after both api responses
            const localization = await aemClientService.getLocalization(marketService.getMarketCode(), languageCode);
            const content = await aemClientService.initMarketContent(marketService.getMarketCode(), languageCode);

            if (isLoggedIn) {
                await iccMarketService.setConsumerLanguageCode({languageCode, throwException: true});
            }

            await initLocalizations(marketService.getMarketCode(), languageCode, localization, content);

            if (isAem) {
                appRouterService.forwardToRootPage();
                return;
            } else if (isInstallPwaPage) {
                routerService.reload();
                return;
            }

            await tncService.getTncData(null, null, {disableLoader: true});
        } catch (e) {
            appErrorService.showGlobalError();
        }
    };

    return handleLanguageChange;
};

export default useLanguageSelect;
